import { CampaignOutlined, ContentPaste, ExpandMore, Save } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Snackbar, TextField, Typography } from "@mui/material";
import { IMarketplace } from "../../../../interfaces/IMarketplace";
import { useEffect, useState } from "react";
import { useMarketplaces } from "../../../../hooks/useMarketplaces";
import { SecaoConfiguracao } from "../../../../../core/components/SecaoConfiguracao";

interface PixelMetaContainerProps {
    marketplace: IMarketplace;
    refresh: () => void;
    isLoading: boolean;
}

export const PixelMetaContainer = ({ marketplace, refresh, isLoading }: PixelMetaContainerProps) => {
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [pixelScriptInput, setPixelScriptInput] = useState(
        marketplace?.configuracao?.script as string || ""
    );
    const [scriptError, setScriptError] = useState<string | null>(null);

    const { salvar, loading } = useMarketplaces();

    const validarPixelScript = (script: string): boolean => {
        const caracteristicasPixel = [
            "fbq",
            "https://connect.facebook.net/en_US/fbevents.js",
            "fbq('init'",
            "fbq('track'"
        ];

        return caracteristicasPixel.every(caracteristica =>
            script.includes(caracteristica)
        );
    };

    const salvarScript = async () => {
        if (!pixelScriptInput.trim()) {
            setScriptError("O código do Pixel não pode estar vazio");
            return;
        }

        if (!validarPixelScript(pixelScriptInput)) {
            setScriptError("O código inserido não parece ser um script válido do Pixel da Meta. Verifique se você copiou o código completo.");
            return;
        }

        setScriptError(null);

        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                script: pixelScriptInput,
            }
        });

        refresh();
        setSnackbarMessage("Script do Pixel da Meta salvo com sucesso");
        setSnackbarOpen(true);
    };

    const fecharSnackbar = () => {
        setSnackbarOpen(false);
    };

    const colarScript = async () => {
        try {
            const textoCopiado = await navigator.clipboard.readText();
            setPixelScriptInput(textoCopiado);

            if (!validarPixelScript(textoCopiado)) {
                setScriptError("Atenção: O código colado não parece ser um script válido do Pixel da Meta. Verifique se você copiou o código completo.");
            } else {
                setScriptError(null);
                setSnackbarMessage("Código colado com sucesso");
                setSnackbarOpen(true);
            }
        } catch (error) {
            setSnackbarMessage("Não foi possível acessar a área de transferência");
            setSnackbarOpen(true);
        }
    };

    useEffect(() => {
        if (!pixelScriptInput.trim()) {
            setScriptError(null);
            return;
        }

        if (!validarPixelScript(pixelScriptInput)) {
            setScriptError("Atenção: O código digitado não parece ser um script válido do Pixel da Meta. Verifique se você digitou o código completo.");
        } else {
            setScriptError(null);
        }
    }, [pixelScriptInput]);

    const configurado = !!marketplace?.configuracao?.script;

    return (
        <>
            <SecaoConfiguracao
                titulo="Pixel da Meta"
                descricao={configurado ? "Integração ativa no cardápio" : "Não configurado"}
                icone={<CampaignOutlined />}
                corIcone="#1877F2"
                acoes={
                    <>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ContentPaste />}
                            onClick={colarScript}
                        >
                            Colar código
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Save />}
                            onClick={salvarScript}
                            disabled={loading || !pixelScriptInput.trim() || !!scriptError}
                        >
                            Salvar
                        </Button>
                    </>
                }
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: "12px" }}>
                        <Typography variant="caption">
                            Integre o cardápio aos anúncios do Facebook e Instagram para acompanhar
                            conversões e criar públicos personalizados.
                        </Typography>
                    </Alert>

                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        size="small"
                        label="Código do Pixel"
                        variant="outlined"
                        value={pixelScriptInput}
                        onChange={(e) => {
                            setPixelScriptInput(e.target.value);
                        }}
                        placeholder="Cole aqui o código do seu Pixel da Meta..."
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.75rem" } }}
                        InputLabelProps={{ shrink: true }}
                        error={!!scriptError}
                        helperText={scriptError}
                    />

                    <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                            border: theme => `1px solid ${theme.palette.divider}`,
                            "&:before": { display: "none" }
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight={500}>
                                Como obter o código do Pixel
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="caption" component="div" color="text.secondary">
                                1. Acesse a sua conta do Meta Business (business.facebook.com)
                            </Typography>
                            <Typography variant="caption" component="div" color="text.secondary">
                                2. Navegue até Gerenciador de Eventos &gt; Pixels
                            </Typography>
                            <Typography variant="caption" component="div" color="text.secondary">
                                3. Selecione ou crie um Pixel e copie o código base
                            </Typography>
                            <Typography variant="caption" component="div" color="text.secondary">
                                4. Cole o código completo (incluindo as tags &lt;script&gt;) no campo acima
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </SecaoConfiguracao>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={fecharSnackbar}
                message={snackbarMessage}
            />
        </>
    );
};
